import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  StopIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import { translationApi } from '@/services/audioApi';
import AudioStepDetailModal from './AudioStepDetailModal';

interface AudioTranslationProgressProps {
  jobId: string;
  onCancel: () => void;
  onComplete: () => void;
  onClose?: () => void;
}

const AudioTranslationProgress: React.FC<AudioTranslationProgressProps> = ({
  jobId,
  onCancel,
  onComplete,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audio-job', jobId],
    queryFn: () => translationApi.getJobDetails(jobId),
    refetchInterval: (data: any) => {
      const s = data?.data?.status;
      return s === 'processing' || s === 'pending' ? 2000 : false;
    },
    enabled: !!jobId,
  });

  const jobData = data?.data;

  React.useEffect(() => {
    if (jobData?.status === 'completed' || jobData?.status === 'failed') {
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData?.status]);

  if (isLoading || !jobData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const isProcessing = jobData.status === 'processing' || jobData.status === 'pending';
  const isCompleted = jobData.status === 'completed';
  const isFailed = jobData.status === 'failed';

  const getStepIcon = (step: any) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Audio Translation Progress
        </h3>

        <div className="flex items-center gap-2">
          {isProcessing && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Cancel this translation job?')) {
                  onCancel();
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            >
              <StopIcon className="h-4 w-4" />
              <span>Cancel</span>
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {jobData.currentStep}
          </span>
          <span className={`text-sm font-medium ${getStatusColor(jobData.status)}`}>
            {jobData.status.charAt(0).toUpperCase() + jobData.status.slice(1)}
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              isFailed ? 'bg-red-500' : isCompleted ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${jobData.progress}%` }}
          ></div>
        </div>

        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{jobData.progress}% complete</span>
          <span>Job ID: {jobData.jobId}</span>
        </div>
      </div>

      {/* Step Details */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Processing Steps
        </h4>

        {jobData.steps.map((step: any) => {
          const canExpand = (step.status === 'completed' || step.status === 'failed') && !isProcessing;

          return (
            <div key={step.id} className="rounded-lg overflow-hidden border border-transparent">
              <div
                className={`flex items-center space-x-3 p-3 ${
                  step.status === 'processing'
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : step.status === 'completed'
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : step.status === 'failed'
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                } ${canExpand ? 'cursor-pointer hover:opacity-90' : ''} rounded-lg`}
                onClick={() => canExpand && setExpandedStep(step.id)}
              >
                <div className="flex-shrink-0">{getStepIcon(step)}</div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${getStatusColor(step.status)}`}>{step.status}</span>
                      {canExpand && <MagnifyingGlassPlusIcon className="h-4 w-4 text-gray-400" />}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                  {step.error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">Error: {step.error}</p>}
                  {step.duration && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Completed in {Math.round(step.duration / 1000)}s
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {expandedStep && (
        <AudioStepDetailModal
          jobId={jobId}
          job={jobData}
          step={jobData.steps.find((s: any) => s.id === expandedStep)}
          onClose={() => setExpandedStep(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['audio-job', jobId] })}
        />
      )}

      {/* Results */}
      {isCompleted && jobData.result && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
            Translation Completed Successfully!
          </h4>

          <div className="space-y-2 text-sm text-green-700 dark:text-green-300">
            <p>
              <span className="font-medium">Source Language:</span>{' '}
              {jobData.result.sourceLanguage}
            </p>
            <p>
              <span className="font-medium">Target Language:</span>{' '}
              {jobData.result.targetLanguage}
            </p>
            <p>
              <span className="font-medium">Voice:</span>{' '}
              {jobData.result.voiceName}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => translationApi.downloadFile(jobData.result.outputAudioUrl, `audio-translation-${jobId}.${jobData.result.metadata?.audioFormat || 'mp3'}`)}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download Translated Audio
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {isFailed && jobData.error && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
            Translation Failed
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300">
            {jobData.error}
          </p>
        </div>
      )}
    </div>
  );
};

export default AudioTranslationProgress;
